import { TranslationKey } from './../../../../types/translation/TranslationKey';

import React, { useContext, useMemo, useState } from 'react';
import { FlexPane } from '../FlexPane/FlexPane';
import SearchBarWithFilter from '../SearchBarWithFilter/SearchBarWithFilter';
import Table, { TableHeader } from '../../../../ui/components/Table/Table';
import { FileContext } from '../../../../context/FileContext';
import { FileUtils, CreateFileInput, FileEntity, FileEntityType, FileWithSas } from '../../../../types/file';
import { t } from '../../../../types/translation/Translator';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CompanyContext } from '../../../../context/CompanyContext';
import { UserContext } from '../../../../context/UserContext';
import {
  FileMutations,
  CreateFilesResponse,
  CreateFilesVariables,
  FileQueries,
  GetFileDataResponse,
  GetFileDataVariables,
} from '../../../../graphql/file.graphql';
import { DeleteButtonTemplate } from '../Button/Templates';
import DeleteFilesModal from './Modals/DeleteFilesModal';
import FileViewModal from './Modals/FileViewModal';
import { VentoryColor } from '../../../util/color.util';
import UploadFileInput from './UploadFileInput';
import { FileFilter } from '../../filters/FileFilter/FileFilter';
import { useLocation } from 'react-router-dom';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';

interface FilesForEntityPaneProps {
  entityId: string;
  entityType: FileEntityType;
  setError: (error: string) => void;
}

export default function FilesForEntityPane({ entityId, entityType, setError }: FilesForEntityPaneProps) {
  const { state } = useLocation();
  if (state !== null) {
    if (state.entityId && !entityId) entityId = state.entityId;
    if (state.entityType && !entityType) entityType = state.entityType;
    if (state.error) setError(state.error);
  }

  const { currentCompany } = useContext(CompanyContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { fileCache, setFileCache } = useContext(FileContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewedFile, setPreviewedFile] = useState<FileEntity | undefined>(undefined);

  const headers: TableHeader<FileEntity>[] = [
    {
      key: 'name',
      name: t(TranslationKey.name).singular.label,
      text: (item: FileEntity) => item.name,
    },
    {
      key: 'createdBy',
      name: t(TranslationKey.uploadedBy).singular.label,
      text: (item: FileEntity) =>
        companyUsers.get(item.createdBy)?.email || t(TranslationKey.unknownUser).singular.label,
    },
    {
      key: 'createdAt',
      name: t(TranslationKey.uploadedAt).singular.label,
      text: (item: FileEntity) => new Date(item.createdAt).toLocaleDateString(locale),
      sortValue: (item: FileEntity) => item.createdAt,
    },
    {
      key: 'type',
      name: t(TranslationKey.type).singular.label,
      text: (item: FileEntity) => item.fileType,
    },
    {
      key: 'size',
      name: t(TranslationKey.size).singular.label,
      text: (item: FileEntity) => FileUtils.fileSizeToFormattedString(item.size, 0),
      sortValue: (item: FileEntity) => item.size,
    },
  ];

  const { files, filesLoading, setFiles } = useContext(FileContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  const allItems = useMemo(() => {
    return [...files.values()].filter(f => f.entityType === FileEntityType.pmd && f.entityId === entityId);
  }, [files]);

  const [items, setItems] = useState([...files.values()]);
  const [selected, setSelected] = useState(new Set<string>());

  const [create, { loading }] = useMutation<CreateFilesResponse, CreateFilesVariables>(FileMutations.create, {
    onCompleted: result => {
      result.createFiles.forEach(file => files.set(file.id, new FileEntity(file)));
      setFiles(new Map(files));
    },
    onError: error => setError(error.message),
  });
  const [getData] = useLazyQuery<GetFileDataResponse, GetFileDataVariables>(FileQueries.getData);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return setError('Something went wrong uploading your file!');

    const inputs: CreateFileInput[] = await Promise.all(
      [...files].map(file => CreateFileInput.fromFile(file, entityId, entityType, currentCompany.id)),
    );
    await create({ variables: { files: inputs } });
  };

  const handleFilePreview = async (file: FileEntity) => {
    if (file.primaryFileType === 'video' || file.primaryFileType === 'image') {
      setPreviewOpen(true);
      setPreviewedFile(file);
    } else {
      const cachedFileSas = fileCache.get(file.id);
      if (cachedFileSas) return window.open(cachedFileSas.signature);

      setPreviewOpen(false);
      setPreviewedFile(undefined);
      await getData({
        variables: { companyId: file.companyId, entityType: file.entityType, id: file.id },
        onCompleted: response => {
          setFileCache(new Map(fileCache).set(file.id, new FileWithSas(response.fileSas)));
          window.open(response.fileSas.signature);
        },
        onError: error => setError(error.message),
      });
    }
  };

  const buttons = useMemo(() => {
    const shown = [];

    if (selected.size)
      shown.push(
        DeleteButtonTemplate(() => setDeleteModalOpen(true), {
          badge: { backgroundColor: VentoryColor.red700, content: selected.size },
        }),
      );

    return shown;
  }, [selected]);

  const filter = new FileFilter();

  return (
    <>
      <DeleteFilesModal
        entityType={FileEntityType.pmd}
        open={deleteModalOpen}
        setOpen={open => {
          setDeleteModalOpen(open);
        }}
        ids={[...selected]}
      />

      {previewedFile ? <FileViewModal open={previewOpen} setOpen={setPreviewOpen} file={previewedFile} /> : null}

      <FlexPane
        header={
          isAdmin ? (
            <UploadFileInput
              multiple
              onFile={handleFiles}
              file={undefined}
              loading={loading}
              height='150px'
              fileEntityType={entityType}
              subText={t(TranslationKey.supportedFilesCanBeImagesVideoOrDocuments).singular.upper}
            />
          ) : null
        }
        content={
          <FlexPane
            header={
              <SearchBarWithFilter
                items={allItems}
                setItems={setItems}
                loading={filesLoading}
                placeholder={t(TranslationKey.filterFiles).singular.label}
                buttons={buttons}
                filter={filter}
              />
            }
            content={
              <Table
                items={allItems}
                headers={headers}
                loading={filesLoading}
                filter={filter}
                onSelected={values => (isAdmin ? setSelected(values) : null)}
                onClick={handleFilePreview}
              />
            }
          />
        }
      />
    </>
  );
}
